import type { Ref } from "vue";
import * as Cesium from "cesium";

type CartoStyle = "positron" | "darkmatter" | "voyager";
export type BasemapType = "none" | "satellite" | CartoStyle;

interface UseBasemapOptions {
    viewer: Ref<Cesium.Viewer | null>;
    selectedBasemap: Ref<BasemapType>;
}

export function useBasemap({ viewer, selectedBasemap }: UseBasemapOptions) {
    let basemapLayer: Cesium.ImageryLayer | null = null;

    const createCartoBasemap = (style: CartoStyle) => {
        const urls = {
            positron:
                "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png",
            darkmatter:
                "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
            voyager:
                "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png",
        } as const;

        return new Cesium.UrlTemplateImageryProvider({
            url: urls[style],
            subdomains: ["a", "b", "c", "d"],
            credit: "© OpenStreetMap contributors & CARTO",
        });
    };

    const addSatelliteImagery = async () => {
        if (!viewer.value) return;
        try {
            const imageryProvider = await Cesium.IonImageryProvider.fromAssetId(2);
            basemapLayer = viewer.value.imageryLayers.addImageryProvider(imageryProvider, 1);
        } catch (error) {
            console.error("Error loading Cesium Ion imagery:", error);
        }
    };

    const changeBasemap = async (): Promise<void> => {
        if (!viewer.value) return;

        const v = viewer.value;
        if (basemapLayer) {
            v.imageryLayers.remove(basemapLayer, true);
            basemapLayer = null;
        }

        if (selectedBasemap.value === "none") {
            v.scene.globe.show = false;
            return;
        }

        v.scene.globe.show = true;

        if (selectedBasemap.value === "satellite") {
            await addSatelliteImagery();
        } else {
            // CartoDB basemap
            const cartoProvider = createCartoBasemap(
                selectedBasemap.value as CartoStyle
            );
            basemapLayer = v.imageryLayers.addImageryProvider(cartoProvider, 1);
        }
    };

    return {
        changeBasemap,
        addSatelliteImagery,
    };
}
